import { COLS_BAR_HEIGHT } from './consts';
import {
  $tempSelection,
  $tempUnion,
  addTempSelection,
  clearSelections,
  setTempEnd,
  useColSelected,
  useSelectionInCol,
} from './selection';
import { RectRange } from '@/model/range';
import type { Sheet } from '@/model/sheet';
import type { Unit } from '@/model/types';
import { undoManager } from '@/undo';
import { numberToColumn } from '@/utils/utils';
import { Vector2 } from '@/utils/vec';
import { useStore } from '@nanostores/react';
import clsx from 'clsx';
import { atom } from 'nanostores';
import { type ReactNode, useCallback } from 'react';

export const $dragSelecting = atom(false);

function ColView({
  posX,
  colIndex,
  onSetColWidth,
  sheet,
}: {
  posX: number;
  colIndex: number;
  onSetColWidth: (colIndex: number, width: Unit) => void;
  sheet: Sheet;
}) {
  const dragging = useStore($dragSelecting);
  const selected = useColSelected(colIndex);
  const inSelection = useSelectionInCol(colIndex);
  const width = sheet.getColWidth(colIndex);

  function onHandleMouseDown(e: React.MouseEvent) {
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = width;

    const tx = undoManager.startTransaction(() =>
      onSetColWidth(colIndex, startWidth),
    );

    function onMouseMove(e: MouseEvent): number {
      const dx = e.clientX - startX;
      const newWidth = Math.max(startWidth + dx, 0);
      onSetColWidth(colIndex, newWidth);
      return newWidth;
    }

    function onMouseUp(e: MouseEvent) {
      const newWidth = onMouseMove(e);
      if (newWidth !== startWidth) {
        tx.commit(
          () => onSetColWidth(colIndex, newWidth),
          `Resize column ${numberToColumn(colIndex)}`,
        );
      } else {
        tx.cancel();
      }

      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!e.shiftKey) {
        clearSelections();
      }
      $tempSelection.set(
        RectRange.cols(new Vector2(colIndex, -Infinity), colIndex),
      );
      $tempUnion.set(!selected || !e.shiftKey);
      $dragSelecting.set(true);

      function onMouseUp() {
        $dragSelecting.set(false);
        addTempSelection();
        window.removeEventListener('mouseup', onMouseUp);
      }
      window.addEventListener('mouseup', onMouseUp);
    },
    [colIndex, selected],
  );

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!$dragSelecting.get()) return;
      setTempEnd(new Vector2(colIndex, Infinity));
    },
    [colIndex],
  );

  function onDoubleClick() {
    onSetColWidth(colIndex, 'auto');
    undoManager.push({
      undo: () => onSetColWidth(colIndex, width),
      redo: () => onSetColWidth(colIndex, 'auto'),
      description: `Auto resize column ${numberToColumn(colIndex)}`,
    });
  }

  if (width === 0) {
    return (
      <div
        className="group absolute top-0 z-2 flex -translate-x-1/2 items-center justify-center border-r border-b border-l border-black bg-white"
        style={{
          left: posX,
          width: 4,
          height: COLS_BAR_HEIGHT,
        }}
      >
        <div
          className="pointer-events-auto absolute top-full left-0 hidden h-2 w-8 -translate-x-1/2 bg-black/50 group-hover:block"
          onClick={onDoubleClick}
        ></div>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'absolute top-0 flex items-center justify-center border-b border-l border-r-black/50 bg-gray-100 select-none',
        selected && 'bg-red-800 font-bold text-white',
        inSelection && !selected && 'bg-gray-200',
      )}
      style={{
        left: posX,
        width,
        height: COLS_BAR_HEIGHT,
      }}
      onMouseDown={onMouseDown}
    >
      {dragging && (
        <div
          className="absolute top-[-100vh] right-0 bottom-[-100vh] left-0 z-1"
          onMouseMove={onMouseMove}
        ></div>
      )}
      {numberToColumn(colIndex + 1)}
      <div
        className={clsx(
          'absolute top-0 right-0 bottom-0 z-1 w-2 translate-x-1/2 cursor-ew-resize',
          'after:absolute after:top-0 after:right-1/2 after:bottom-0 after:w-0 after:translate-x-1/2 after:bg-black/50 after:transition-all hover:after:w-full',
        )}
        onMouseDown={onHandleMouseDown}
        onDoubleClick={onDoubleClick}
      ></div>
    </div>
  );
}

export function ColsView({
  posX,
  startCol,
  endCol,
  onSetColWidth,
  sheet,
}: {
  posX: number;
  startCol: number;
  endCol: number;
  onSetColWidth: (colIndex: number, width: Unit) => void;
  sheet: Sheet;
}) {
  const cols: ReactNode[] = [];

  let currPosX = posX;

  for (let i = startCol; i <= endCol; i++) {
    cols.push(
      <ColView
        key={i}
        posX={currPosX}
        colIndex={i}
        onSetColWidth={onSetColWidth}
        sheet={sheet}
      />,
    );
    currPosX += sheet.getColWidth(i);
  }

  return <div className="sticky top-0">{cols}</div>;
}
